import * as d3 from 'd3'
import _ from 'lodash'
import React, { useState, useEffect } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'

import i18n from 'live/services/i18n'

import { MiddlePulse, TopBar } from '../../../../../BowtieManagment/components/Bowtie/components'
import { ZoomHandler } from '../../../../../generic'
import { IReduxActions } from '../../../../../store'
import { IReduxStoreReducers, IBarrier, IBowtie, IBarrierTypeIcon } from '../../../../../types'
import { d3PromiseSelect } from '../../../../../utils'
import {
    topEventRadius,
    initialThreat,
    initialConsequence,
    threatConsequenceHeight,
    hazardBoxWidth,
    shadowOpacity,
    settingsZoomLevel,
    THREAT_CONSEQUENCE_GAP_Y
} from '../../../../../utils/constants'
import { IConsequenceEditor, IThreatEditor } from '../../BowtieEditor'
import { AddThreatConsequence, ThreatandConsequenceEditor } from './components'
import { BowTieStyledContainer, ShowFrequencyButton } from './styles'

interface Props {
    width: number
    height: number
    bowtieData: IBowtie
    bowtieCode: string
    onEditClick: (barrier: IBarrier, type: IType, escId: string) => void
    onDeleteClick: () => void
    selectedBarrier: IBarrier
    onEditThreatConsequence: (item: IThreatEditor | IConsequenceEditor, type: IType) => void
    selectedThreatConsequence: IThreatEditor | IConsequenceEditor
    handleChange: (
        dnewBarrier?: IBarrier,
        isThreatConsequenceMandatory?: boolean,
        isTopEventMandatory?: boolean
    ) => void
    handleDeleteThreatConsequenceClick: (type: IType, index: number) => void
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}
interface Vector3 {
    x: number
    y: number
    k: number
}

type IType = 'threat' | 'consequence'

export const BowtieEditorSVG: React.FC<Props> = ({
    height,
    width,
    bowtieData,
    bowtieCode,
    onEditClick,
    onDeleteClick,
    selectedBarrier,
    selectedThreatConsequence,
    onEditThreatConsequence,

    handleChange,
    handleDeleteThreatConsequenceClick,
    barrierTypeIconMap
}) => {
    const [bowtieSVG, setBowtieSVG] = useState<d3.Selection<SVGSVGElement, unknown, HTMLElement, any>>(null) // eslint-disable-line @typescript-eslint/no-explicit-any
    const [zoomBehaviour, setZoomBehaviour] = useState<d3.ZoomBehavior<Element, unknown>>(null)
    const [consequenceX, setConsequenceX] = useState(initialConsequence)
    const [threatX, setThreatX] = useState(initialThreat)
    const [showFrequency, setShowFrequency] = useState<boolean>(false)

    const [transalation, setTranslation] = useState<Vector3>({
        x: width / 2,
        y: height / 2,
        k: 1
    })
    const [closeAll, setCloseAll] = useState<string>(undefined)
    const dispatch = useDispatch()
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxStoreReducers) => state.bowtieExpandedBarriers
    )
    const handleCloseAllProcedure = (type: IType, escId: string) => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        const newExpandedBarriers = expandedBarriers.filter(barr => !barr.barrierId.startsWith(escId))
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: newExpandedBarriers
            }
        })
        setCloseAll(escId)
    }

    const handleShowFrequency = (): void => {
        setShowFrequency(!showFrequency)
    }

    const buttonHideAndDisplayFrequency = (
        <Popover id="popover-trigger-hover-focus">
            {showFrequency
                ? i18n('Hide frequency on threats and consequences')
                : i18n('Show frequency on threats and consequences')}
        </Popover>
    )

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (bowtieData?.methodType === 'SEMI_QUALITATIVE') setShowFrequency(false)
        initd3Listeners()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const allX = []
        threatsExpandedBarriers.forEach(e => e.escalations.forEach(es => allX.push(es.x)))
        allX.sort((a, b) => a - b)

        if (allX.length && allX[0] < initialThreat) {
            setThreatX(allX[0])
        } else {
            setThreatX(initialThreat)
        }
    }, [threatsExpandedBarriers]) // eslint-disable-line react-hooks/exhaustive-deps

    // This useEffect recalculate the Consequence maximum X
    useEffect(() => {
        const allX = []
        consequencesExpandedBarriers.forEach(e => e.escalations.forEach(es => allX.push(es.x)))
        allX.sort((a, b) => b - a)

        if (allX.length && allX[0] > initialConsequence) {
            setConsequenceX(allX[0])
        } else {
            setConsequenceX(initialConsequence)
        }
    }, [consequencesExpandedBarriers]) // eslint-disable-line react-hooks/exhaustive-deps

    const initd3Listeners = async (): Promise<void> => {
        const svg = await d3PromiseSelect<SVGSVGElement, unknown>('#bowtie-svg')

        const zoomHandler = d3
            .zoom()
            .on('zoom', () => setTranslation({ ...d3.event.transform }))
            .scaleExtent([settingsZoomLevel[0], settingsZoomLevel[1]])

        setTimeout(() => {
            zoomHandler(svg)
            svg.on('dblclick.zoom', null)

            svg.call(zoomHandler.transform, d3.zoomIdentity.translate(width / 2, height / 2 + 30).scale(0.78))
            setZoomBehaviour(() => zoomHandler)
            setBowtieSVG(svg)
        }, 400)
    }

    const getThreatHeight = (): number => {
        if (bowtieData) {
            const threatIndex = bowtieData.topEvent.threats.length - 1

            const threatsHeights = []
            const element: IThreatEditor = bowtieData.topEvent.threats[threatIndex]

            threatsExpandedBarriers
                .filter(el => el.barrierId === `thr-${element?.id ?? element?.uid}`)
                .forEach(e => e.escalations.forEach(es => threatsHeights.push(es.y)))

            const highThreatHeight = threatsHeights.sort((a, b) => b - a)[0] ?? 0

            return highThreatHeight
        }

        return 0
    }

    const getConsequenceHeight = (): number => {
        if (bowtieData) {
            const consequenceIndex = bowtieData.topEvent.consequences.length - 1

            const consequencesHeights = []
            const element: IConsequenceEditor = bowtieData.topEvent.consequences[consequenceIndex]

            consequencesExpandedBarriers
                .filter(el => el.barrierId === `cnq-${element.id ?? element?.uid}`)
                .forEach(e => e.escalations.forEach(es => consequencesHeights.push(es.y)))

            const highConsequenceHeight = consequencesHeights.sort((a, b) => b - a)[0] ?? 0

            return highConsequenceHeight
        }

        return 0
    }

    const getYPosition = (type: 'threat' | 'consequence', index: number): number => {
        if (bowtieData) {
            const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

            const arr = type === 'threat' ? bowtieData.topEvent.threats : bowtieData.topEvent.consequences

            const heights = []
            if (index > 0) {
                expandedBarriers
                    .filter(el => {
                        const prevElement: IThreatEditor | IConsequenceEditor = arr[index - 1]

                        return el.barrierId.startsWith(
                            `${type === 'threat' ? 'thr' : 'cnq'}-${prevElement?.id ?? prevElement?.uid}`
                        )
                    })
                    .forEach(e => e.escalations.forEach(es => heights.push(es.y)))
            }

            const sortedHeigths = [...heights].sort((a, b) => b - a)

            const prevThreatsHighHeight = sortedHeigths[0]

            return heights.length > 0 ? prevThreatsHighHeight + threatConsequenceHeight + THREAT_CONSEQUENCE_GAP_Y : 0
        }

        return 0
    }

    const handleAddThreatConsequenceClick = (
        threatOrConsequencePointer: IThreatEditor[] | IConsequenceEditor[],
        type: IType
    ): void => {
        const itemToAdd = {
            barriers: [],
            dependentBarriers: [],
            id: null,
            initialFrequency: null,
            thresholdFrequency: null,
            uid: _.uniqueId('u'),
            name: ''
        }

        threatOrConsequencePointer.push(itemToAdd)

        const dispatchThreatConsequenceId = type === 'threat' ? `thr-${itemToAdd.uid}` : `cnq-${itemToAdd.uid}`

        const xPosition = type === 'threat' ? threatX : consequenceX

        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'ADD_THREAT_EXPANDED_BARRIERS' : 'ADD_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: [
                    {
                        barrierId: dispatchThreatConsequenceId,
                        rootXIndex: threatOrConsequencePointer.length,
                        escalations: [
                            {
                                escId: dispatchThreatConsequenceId,
                                x: xPosition,
                                y: getYPosition(type, threatOrConsequencePointer.length)
                            }
                        ]
                    }
                ]
            }
        })

        handleChange()

        onEditThreatConsequence(threatOrConsequencePointer[threatOrConsequencePointer.length - 1], type)
    }

    const drawShadow = (): JSX.Element => {
        const centerOfTopEvent = transalation.y * (1 / transalation.k)
        const relativeTopEventRadius = topEventRadius * transalation.k

        return (
            <g transform={`translate(${transalation.x - relativeTopEventRadius}, 0) scale(${transalation.k})`}>
                <svg
                    width={hazardBoxWidth}
                    height={centerOfTopEvent > 0 ? centerOfTopEvent : 0}
                    viewBox={`0 0 ${hazardBoxWidth} ${centerOfTopEvent > 0 ? centerOfTopEvent : 0}`}
                    fill="none"
                    opacity={shadowOpacity}
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d={`M${hazardBoxWidth / 2} ${centerOfTopEvent}L${hazardBoxWidth} 0H0.0205078L${hazardBoxWidth /
                            2} ${centerOfTopEvent}Z`}
                        fill="#DDDEE1"
                    />
                </svg>
            </g>
        )
    }

    function checkBarriers(barrierId: string, array: string[]): boolean {
        for (const item of array) {
            if (barrierId.split('|').length === 1 || barrierId === item || barrierId.startsWith(`${item}|`)) {
                return true
            }
        }

        return false
    }

    const handleDispatch = (): void => {
        if (bowtieData && consequencesExpandedBarriers?.length && threatsExpandedBarriers?.length) {
            const { consequences, threats } = bowtieData.topEvent

            const thrBarriers = threats
                .map((thr: IThreatEditor) => {
                    const id = thr.id ?? thr.uid

                    return [`thr-${id}`, ...thr.barriers.map(bar => `thr-${id}|bar-${bar.id}`)]
                })
                .reduce((prev, curr) => [...prev, ...curr], [])

            const cnqBarriers = consequences
                .map((cnq: IConsequenceEditor) => {
                    const id = cnq.id ?? cnq.uid

                    return [`cnq-${id}`, ...cnq.barriers.map(bar => `cnq-${id}|bar-${bar.id}`)]
                })
                .reduce((prev, curr) => [...prev, ...curr], [])

            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: 'SET_THREAT_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: threatsExpandedBarriers.filter(e => checkBarriers(e.barrierId, thrBarriers))
                }
            })

            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: consequencesExpandedBarriers.filter(e =>
                        checkBarriers(e.barrierId, cnqBarriers)
                    )
                }
            })
        }
    }

    useEffect(() => {
        handleDispatch()
    }, [bowtieData]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <BowTieStyledContainer>
                <svg id="bowtie-svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                    <defs>
                        <filter id="blur" x="-50" y="-50" width="200" height="200">
                            <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
                        </filter>
                    </defs>
                    {drawShadow()}
                    <g
                        id="bowtie-container"
                        transform={`translate(${transalation.x} ${transalation.y}) scale(${transalation.k})`}
                    >
                        {/* threat side */}
                        <g transform={`translate(0, ${-getThreatHeight() / 2})`}>
                            {bowtieData?.topEvent.threats.map((threat: IThreatEditor, index, arr) => (
                                <ThreatandConsequenceEditor
                                    key={`thr-${threat.id ?? threat.uid}`}
                                    uid={`thr-${threat.id ?? threat.uid}`}
                                    type="threat"
                                    threatConsequenceData={threat}
                                    middleY={getThreatHeight() / 2}
                                    x={threatX}
                                    y={getYPosition('threat', index)}
                                    rootXIndex={index}
                                    onEditClick={onEditClick}
                                    onDeleteClick={onDeleteClick}
                                    selectedBarrier={selectedBarrier}
                                    handleCloseAllProcedure={handleCloseAllProcedure}
                                    closeAll={closeAll}
                                    setCloseAll={setCloseAll}
                                    selectedThreatConsequence={selectedThreatConsequence}
                                    onEditThreatConsequence={onEditThreatConsequence}
                                    handleDeleteThreatConsequenceClick={handleDeleteThreatConsequenceClick}
                                    canDelete={arr.length > 1}
                                    barrierTypeIconMap={barrierTypeIconMap}
                                    showFrequency={showFrequency}
                                />
                            ))}
                        </g>
                        <AddThreatConsequence
                            type="threat"
                            x={threatX}
                            y={getThreatHeight() / 2}
                            onAddRequest={() =>
                                handleAddThreatConsequenceClick(bowtieData?.topEvent?.threats, 'threat')
                            }
                            threatConsequenceLength={bowtieData?.topEvent?.threats?.length ?? 0}
                        />
                        {/* consequence side */}
                        <g transform={`translate(0, ${-getConsequenceHeight() / 2})`}>
                            {bowtieData?.topEvent.consequences.map((consequence: IConsequenceEditor, index, arr) => (
                                <ThreatandConsequenceEditor
                                    key={`cnq-${consequence.id ?? consequence.uid}`}
                                    uid={`cnq-${consequence.id ?? consequence.uid}`}
                                    type="consequence"
                                    threatConsequenceData={consequence}
                                    middleY={getConsequenceHeight() / 2}
                                    x={consequenceX}
                                    y={getYPosition('consequence', index)}
                                    rootXIndex={index}
                                    onEditClick={onEditClick}
                                    onDeleteClick={onDeleteClick}
                                    selectedBarrier={selectedBarrier}
                                    handleCloseAllProcedure={handleCloseAllProcedure}
                                    closeAll={closeAll}
                                    setCloseAll={setCloseAll}
                                    handleDeleteThreatConsequenceClick={handleDeleteThreatConsequenceClick}
                                    selectedThreatConsequence={selectedThreatConsequence}
                                    onEditThreatConsequence={onEditThreatConsequence}
                                    canDelete={arr.length > 1}
                                    barrierTypeIconMap={barrierTypeIconMap}
                                    showFrequency={showFrequency}
                                />
                            ))}
                        </g>
                        <AddThreatConsequence
                            type="consequence"
                            x={consequenceX}
                            y={getConsequenceHeight() / 2}
                            onAddRequest={() =>
                                handleAddThreatConsequenceClick(bowtieData?.topEvent?.consequences, 'consequence')
                            }
                            threatConsequenceLength={bowtieData?.topEvent?.consequences?.length ?? 0}
                        />
                        <MiddlePulse showEvents={false} event={null} title={bowtieData?.topEvent.name} />
                    </g>
                    <TopBar
                        hazard={bowtieData?.topEvent?.hazard.name}
                        relativeMiddleX={transalation.x}
                        relativeMiddleY={transalation.y}
                        widgetWidth={width}
                        consequencesX={consequenceX + transalation.x}
                        threatsX={threatX - transalation.x}
                        scale={transalation.k}
                        bowtieCode={bowtieCode}
                    />
                </svg>
            </BowTieStyledContainer>
            <ZoomHandler scale={transalation.k} zoomBehaviour={zoomBehaviour} target={bowtieSVG} />
            {bowtieData?.methodType !== 'SEMI_QUALITATIVE' ? (
                <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={buttonHideAndDisplayFrequency}>
                    <ShowFrequencyButton onClick={handleShowFrequency}>
                        {showFrequency ? <i className="fas fa-eye"></i> : <i className="fas fa-eye-slash"></i>}
                    </ShowFrequencyButton>
                </OverlayTrigger>
            ) : null}
        </>
    )
}
